import moment from 'moment';
import { ParamMap, Params } from '@angular/router';
import { IBase } from 'src/app/models';

export function hasAnyFilterKey(params: ParamMap, keys: readonly string[]): boolean {
  return keys.some(k => params.has(k));
}

export function serializeIdList(items: { id?: number }[] | undefined): string | null {
  if (!items?.length) return null;
  return items.map(i => i.id).filter(id => id != null).join(',');
}

export function serializeStringList<T extends string>(items: T[] | undefined): string | null {
  if (!items?.length) return null;
  return items.join(',');
}

export function serializeNumberList(items: number[] | undefined): string | null {
  if (!items?.length) return null;
  return items.filter(n => n != null && !isNaN(n)).join(',');
}

export function parseIdList<T extends IBase = IBase>(raw: string | null, fallback?: T[]): T[] {
  if (!raw) return [];
  const ids = raw.split(',').map(s => Number(s)).filter(n => !isNaN(n));
  return ids.map(id => fallback?.find(b => b.id === id) ?? ({ id } as T));
}

export function parseId(raw: string | null): number | null {
  if (!raw) return null;
  const id = Number(raw);
  return isNaN(id) ? null : id;
}

export function parseNumberList(raw: string | null): number[] {
  if (!raw) return [];
  return raw.split(',').map(s => Number(s)).filter(n => !isNaN(n));
}

export function parseEnumList<T extends string>(raw: string | null, defaultValue: T[] = []): T[] {
  if (!raw) return defaultValue;
  return raw.split(',').filter(s => s.length > 0) as T[];
}

export function parseDate(raw: string | null): Date | undefined {
  if (!raw) return undefined;
  const parsed = moment(raw, 'YYYY-MM-DD');
  return parsed.isValid() ? parsed.toDate() : undefined;
}

export function parseStoredDate(raw: unknown): Date | undefined {
  if (!raw) return undefined;
  const parsed = moment(raw as string);
  return parsed.isValid() ? parsed.toDate() : undefined;
}

export function buildQueryString(params: Params, excludedKeys: readonly string[] = []): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (excludedKeys.includes(key)) continue;
    if (Array.isArray(value)) {
      (value as string[]).forEach(v => parts.push(`${key}=${encodeURIComponent(v)}`));
    } else if (value !== null && value !== undefined) {
      parts.push(`${key}=${encodeURIComponent(value as string)}`);
    }
  }
  return parts.length ? `?${parts.join('&')}` : '';
}
